import { SpineGameObject } from "@esotericsoftware/spine-phaser";
import Phaser from "phaser";
import FreeSpinReel from "./FreeSpinReel";
import ResourcesDispatcher from "../../config/ResourcesDispatcher";
import { LayoutBuilder } from "../buildLayoutTree";

const MULTIPLIERS_ANIMATIONS: Record<number, string> = {
    1: "1X",
    2: "2X",
    3: "3X",
    4: "4X",
    5: "5X",
    6: "10X",
    7: "20X",
    8: "50X"
};

const MULTIPLIERS_STATIC: Record<number, string> = {
    1: "1X_static",
    2: "2X_static",
    3: "3X_static",
    4: "4X_static",
    5: "5X_static",
    6: "10X_static",
    7: "20X_static",
    8: "50X_static"
};

export default class FreeSpinCell {
    public scene: any; // Generico per evitare dipendenze circolari
    public gameLogicVariableManager: any;
    public freeSpinReel: FreeSpinReel | null;
    public reelIndex: number;

    private container: Phaser.GameObjects.Container;
    private freeSpinContainer: Phaser.GameObjects.Container;

    private on: boolean = true;
    private memo: number = 0;
    private index: number;
    
    // public x: number;
    // public y: number;

    static OnOffAnimationTrack: number = 0;
    static WinAnimationTrack: number = 0;
    static OnOffAnimationAlpha: number = 1;
    static WinAnimationAlpha: number = 1;

    private freeSpinSymbolImageRaw: any; //simbolo off raw
    private freeSpinSymbolSpineRaw: any; //simboli spine raw

    private freeSpinSymbolImage: Phaser.GameObjects.Image;
    private freeSpinSymbolSpine: SpineGameObject;
    private layoutBuilder: LayoutBuilder;

    public constructor(scene: any, gameLogicVariableManager: any, _x: number, _y: number, index: number, reelIndex: number, layoutBuilder: LayoutBuilder) {
        this.scene = scene;
        this.gameLogicVariableManager = gameLogicVariableManager;
        this.reelIndex = reelIndex
        this.freeSpinReel = null;
        this.layoutBuilder = layoutBuilder;

        this.index = index;

        let cellGeom = this.layoutBuilder.getNodeByPath(`FreeSpin/grid/reel_${this.reelIndex}/cell_${index}/cell#geom`, 'Landscape');
        
        this.freeSpinSymbolImageRaw = ResourcesDispatcher.GAME_CONSTANTS.FS_SYMBOL_OFF;
        this.freeSpinSymbolSpineRaw = ResourcesDispatcher.SPINES.Moltiplicatori;

        this.freeSpinSymbolImage = this.scene.add.image(cellGeom?.phaser.display?.x, cellGeom?.phaser.display?.y, this.freeSpinSymbolImageRaw.key, this.freeSpinSymbolImageRaw.frame).setTint(0x7F7F7F)
        .setOrigin(cellGeom?.phaser.display?.originX, cellGeom?.phaser.display?.originY);
        
        this.freeSpinSymbolSpine = this.scene.add.spine(cellGeom?.phaser.display?.x, cellGeom?.phaser.display?.y, this.freeSpinSymbolSpineRaw.jsonKey, this.freeSpinSymbolSpineRaw.atlasKey).setOrigin(cellGeom?.phaser.display?.originX, cellGeom?.phaser.display?.originY).setVisible(false);

        this.freeSpinContainer = this.scene.add.container(0, 0,  [this.freeSpinSymbolImage, this.freeSpinSymbolSpine]).setVisible(false);
        this.container = this.layoutBuilder.findByPath(`FreeSpin/grid/reel_${this.reelIndex}/cell_${index}`) as Phaser.GameObjects.Container;
        this.container.add(this.freeSpinContainer);
    }

    /** PUBLIC GETTERS **/
    public getOn(): boolean {
        return this.on;
    }

    public getMemo(): number {
        return this.memo;
    }

    public getContainer(): Phaser.GameObjects.Container {
        return this.container;
    }

    public getIndex(): number {
        return this.index;
    }
    /*******************/

    public setMemo(memo: number) {
        this.memo = memo;
    }
    
    public setIndex(index: number) {
        this.index = index;
    }

    public setOn(on: boolean) {
        this.on = on;
    }

    public turnOn() {
        this.on = true;
    }

    public turnOff() {
        this.on = false;
    }

    public setFreeSpinReel(freeSpinReel: any): void {
        this.freeSpinReel = freeSpinReel;
    }

    public resetLayer() {
        if (!this.freeSpinReel) {
            return;
        }
        if (!this.freeSpinReel.getContainer().exists(this.container)) {
            this.freeSpinReel.getContainer().add(this.container);
        }
    }

    public showFrame() {}

    public hideFrame() {}

    public checkLayer() {}

    public setCellInfo(cellMemo: number) { 
        this.freeSpinContainer.setVisible(true);
        this.freeSpinSymbolImage.setVisible(true);

        const memo = ResourcesDispatcher.GAME_CONSTANTS.MAP_FREESPIN_MEMO_TO_GC_MEMO[cellMemo] ?? 0; // fallback to 0 if not mapped
        this.setMemo(memo);
        
        if(this.getMemo() !== 0) {
            // Mostra spine con animazione STATICA come "posa"
            this.freeSpinSymbolImage.setVisible(false);
            this.freeSpinSymbolSpine.setVisible(true);

            const staticAnimationName = MULTIPLIERS_STATIC[this.getMemo()];
            if (staticAnimationName) {
                // Usa l'animazione statica (es. "1X_static") come posa iniziale
                this.freeSpinSymbolSpine.animationState.setAnimation(0, staticAnimationName, false);
            }
        }
        else {
            // Simbolo vuoto
            this.freeSpinSymbolSpine.setVisible(false);
            this.freeSpinSymbolImage.setVisible(true);
            this.freeSpinSymbolImage.setTexture(this.freeSpinSymbolImageRaw.key, this.freeSpinSymbolImageRaw.frame);
        }
    }

    public playAnimation(loop: boolean = false): Promise<boolean> {
        return new Promise((resolve) => {

            // Guard clause 1: Nessun moltiplicatore
            if (this.getMemo() === 0) {
                this.freeSpinSymbolSpine.setVisible(false);
                resolve(true);
                return;
            }

            // Guard clause 2: Animazione non trovata
            const animationName = MULTIPLIERS_ANIMATIONS[this.getMemo()];
            if (!animationName) {
                resolve(true);
                return;
            }

            this.freeSpinSymbolSpine.setVisible(true);
            const trackEntry = this.freeSpinSymbolSpine.animationState.setAnimation(FreeSpinCell.WinAnimationTrack, animationName, loop);
            trackEntry.alpha = FreeSpinCell.WinAnimationAlpha;

            if (loop) {
                resolve(true);
            } else {
                trackEntry.listener = {
                    complete: () => {
                        const staticAnimationName = MULTIPLIERS_STATIC[this.getMemo()];
                        if (staticAnimationName) {
                            this.freeSpinSymbolSpine.animationState.setAnimation(0, staticAnimationName, false);
                        }
                        resolve(true);
                    }
                };
            }
        });
    }
}